import { Box, Button, Typography } from '@mui/joy'
import { Component, ErrorInfo, ReactNode } from 'react'
import { reportError } from '../utils/errorReporter'

interface Props {
    children: ReactNode
    fallback?: ReactNode
    /** When any of these values change while the boundary is showing an error, the boundary resets (react-error-boundary pattern). */
    resetKeys?: unknown[]
}

interface State {
    hasError: boolean
    error: Error | null
    reloadCoolingDown: boolean
}

const shallowArrayEqual = (a: unknown[] | undefined, b: unknown[] | undefined): boolean => {
    if (a === b) return true
    if (!a || !b || a.length !== b.length) return false
    return a.every((value, index) => Object.is(value, b[index]))
}

const isChunkLoadError = (error: Error): boolean => {
    return (
        error.name === 'ChunkLoadError' ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Failed to load module script') ||
        error.message.includes("Unexpected token '<'")
    )
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        reloadCoolingDown: false,
    }

    private popstateListener: (() => void) | null = null

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, reloadCoolingDown: false }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('[App ErrorBoundary] CAUGHT ERROR:', error.message, error.stack)
        console.error('[App ErrorBoundary] Component stack:', errorInfo.componentStack)

        if (!isChunkLoadError(error)) {
            reportError({
                code: 'ERR_GENERIC_01',
                errorMessage: error.message,
                stackTrace: error.stack,
                metadata: { componentStack: errorInfo.componentStack?.slice(0, 300) },
            })
        }
    }

    public componentDidUpdate(prevProps: Props) {
        // Reset the error state when a resetKey changes (e.g. route navigation) so one
        // crashed page doesn't leave the whole dashboard on the fallback until a reload.
        if (this.state.hasError && this.props.resetKeys && !shallowArrayEqual(prevProps.resetKeys, this.props.resetKeys)) {
            this.handleReset()
        }
    }

    public componentWillUnmount() {
        if (this.popstateListener) {
            window.removeEventListener('popstate', this.popstateListener)
        }
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: null })
    }

    private handleReload = () => {
        const COOLDOWN_KEY = 'chunk-reload-at'
        const COOLDOWN_MS = 10_000
        const last = Number(sessionStorage.getItem(COOLDOWN_KEY) ?? 0)
        const elapsed = Date.now() - last
        if (elapsed < COOLDOWN_MS) {
            this.setState({ reloadCoolingDown: true })
            setTimeout(() => this.setState({ reloadCoolingDown: false }), COOLDOWN_MS - elapsed)
            return
        }
        sessionStorage.setItem(COOLDOWN_KEY, String(Date.now()))
        window.location.reload()
    }

    public render() {
        if (this.state.hasError) {
            const error = this.state.error
            const isChunkError = error && isChunkLoadError(error)

            if (this.props.fallback) {
                return this.props.fallback
            }

            if (isChunkError) {
                const isDark = document.documentElement.classList.contains('dark') || localStorage.getItem('joy-mode') === 'dark'
                return (
                    <div
                        style={{ backgroundColor: isDark ? '#02061799' : '#f1f5f999' }}
                        className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
                        onClick={() => {
                            if (this.popstateListener) {
                                window.removeEventListener('popstate', this.popstateListener)
                            }
                            this.popstateListener = () => {
                                this.handleReset()
                                window.removeEventListener('popstate', this.popstateListener!)
                                this.popstateListener = null
                            }
                            window.addEventListener('popstate', this.popstateListener)
                            window.history.back()
                        }}
                    >
                        <div
                            style={{ backgroundColor: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#f8fafc' : '#0f172a' }}
                            className="w-full max-w-sm rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4 text-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div
                                style={{ backgroundColor: isDark ? '#172554' : '#eff6ff' }}
                                className="flex h-14 w-14 items-center justify-center rounded-full"
                            >
                                <svg
                                    style={{ color: isDark ? '#60a5fa' : '#3b82f6' }}
                                    className="h-7 w-7"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                </svg>
                            </div>
                            <div className="flex flex-col gap-1">
                                <h2 className="text-lg font-semibold">Update Available</h2>
                                <p style={{ color: isDark ? '#94a3b8' : '#64748b' }} className="text-sm">
                                    A new version is available! Refresh the page to get the latest features and improvements.
                                </p>
                            </div>
                            <button
                                onClick={this.handleReload}
                                disabled={this.state.reloadCoolingDown}
                                className={`mt-2 w-full rounded-xl px-4 py-2.5 text-sm font-medium text-white transition-colors ${this.state.reloadCoolingDown ? 'cursor-not-allowed bg-blue-400' : 'cursor-pointer bg-blue-600 hover:bg-blue-700'}`}
                            >
                                {this.state.reloadCoolingDown ? 'Please wait…' : 'Reload Now'}
                            </button>
                        </div>
                    </div>
                )
            }

            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 3,
                        gap: 2,
                    }}
                >
                    <Typography level="h4" color="danger">
                        Something went wrong
                    </Typography>
                    <Typography level="body-md" color="neutral">
                        {error?.message || 'An unexpected error occurred'}
                    </Typography>
                    <Button onClick={this.handleReset} variant="soft" color="primary">
                        Try Again
                    </Button>
                </Box>
            )
        }

        return this.props.children
    }
}
